import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../styles/admin.css';
import { authFetch } from '../services/auth';

interface LandingLead {
  orderReference: string;
  packageId: string;
  packageName: string;
  price: number;
  currency: string;
  source?: string | null;
  goal?: string | null;
  location?: string | null;
  sampleDate?: string | null;
  name: string;
  phone: string;
  email: string;
  company?: string | null;
  comments?: string | null;
  status: string;
  paidAt?: string | null;
  cardPan?: string | null;
  createdAt: string;
}

interface LandingLeadsResponse {
  total: number;
  leads: LandingLead[];
}

const STATUS_PAID = 'PAID';
const STATUS_PENDING = 'PENDING_PAYMENT';

export const AdminLeadsPage: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const [searchParams, setSearchParams] = useSearchParams();
  const refParam = searchParams.get('ref');
  const [leads, setLeads] = useState<LandingLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, refParam]);

  const fetchLeads = async () => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      // When opened from a deep link, ignore the status filter so the
      // referenced lead is always found regardless of its status.
      if (statusFilter && !refParam) {
        queryParams.append('status', statusFilter);
      }

      const response = await authFetch(`/api/admin/landing/leads?${queryParams}`);

      if (!response.ok) {
        throw new Error(`${t('admin:leadsFetchError')}: ${response.status}`);
      }

      const data: LandingLeadsResponse = await response.json();
      setLeads(data.leads);
    } catch (err) {
      console.error('Error fetching landing leads:', err);
      setError(err instanceof Error ? err.message : t('admin:leadsFetchError'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status: string) =>
    status === STATUS_PAID ? 'status-badge status-completed' : 'status-badge status-pending';

  const getStatusDisplayName = (status: string) =>
    status === STATUS_PAID ? t('admin:leadStatusPaid') : t('admin:leadStatusPending');

  const formatMoney = (amount: number, currency: string) => {
    try {
      return (
        new Intl.NumberFormat('uk-UA', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(amount) + ` ${currency === 'UAH' ? 'грн' : currency}`
      );
    } catch {
      return `${amount} ${currency}`;
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleString('uk-UA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const paidCount = leads.filter((l) => l.status === STATUS_PAID).length;
  const visibleLeads = refParam ? leads.filter((l) => l.orderReference === refParam) : leads;

  const clearRef = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('ref');
    setSearchParams(next);
  };

  if (loading && leads.length === 0) {
    return (
      <div className="admin-page">
        <div className="admin-header">
          <h1>{t('admin:leadsTitle')}</h1>
        </div>
        <div className="loading">{t('admin:leadsLoading')}</div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>{t('admin:leadsTitle')}</h1>
        <p className="admin-subtitle">{t('admin:leadsSubtitle')}</p>
      </div>

      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="admin-filters">
        <div className="filter-group">
          <label htmlFor="status-filter">{t('admin:status')}</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="status-filter"
          >
            <option value="">{t('admin:allStatuses')}</option>
            <option value={STATUS_PAID}>{t('admin:leadStatusPaid')}</option>
            <option value={STATUS_PENDING}>{t('admin:leadStatusPending')}</option>
          </select>
        </div>
        <button className="btn btn-secondary" onClick={fetchLeads} style={{ alignSelf: 'flex-end' }}>
          ↻ {t('admin:leadsRefresh')}
        </button>
      </div>

      {refParam && (
        <div
          className="admin-summary"
          style={{ background: 'rgba(41,178,232,.1)', border: '1.5px solid rgba(41,178,232,.4)', borderRadius: 8 }}
        >
          <span>
            {t('admin:leadsShownRef')} <strong>{refParam}</strong>
          </span>
          <button className="btn btn-secondary" onClick={clearRef}>
            {t('admin:leadsShowAll')}
          </button>
        </div>
      )}

      <div className="admin-summary">
        <span>
          {t('admin:leadsFound')} <strong>{leads.length}</strong>
        </span>
        <span>
          {t('admin:leadsPaidCount')} <strong>{paidCount}</strong>
        </span>
      </div>

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin:leadsColReference')}</th>
              <th>{t('admin:leadsColContact')}</th>
              <th>{t('admin:leadsColPackage')}</th>
              <th>{t('admin:columnAmount')}</th>
              <th>{t('admin:leadsColSourceGoal')}</th>
              <th>{t('admin:leadsColPlace')}</th>
              <th>{t('admin:status')}</th>
              <th>{t('admin:leadsColCreated')}</th>
            </tr>
          </thead>
          <tbody>
            {visibleLeads.length === 0 && !loading ? (
              <tr>
                <td colSpan={8} className="no-data">
                  {t('admin:leadsNotFound')}
                </td>
              </tr>
            ) : (
              visibleLeads.map((lead) => (
                <tr key={lead.orderReference}>
                  <td>
                    <div className="order-id">
                      <strong>{lead.orderReference}</strong>
                    </div>
                  </td>
                  <td>
                    <div className="customer-info">
                      <div className="customer-name">{lead.name}</div>
                      <div className="customer-email">{lead.email}</div>
                      <div className="customer-email">{lead.phone}</div>
                      {lead.company && <div className="customer-email">{lead.company}</div>}
                    </div>
                  </td>
                  <td>{lead.packageName}</td>
                  <td>
                    <div className="order-amount">{formatMoney(lead.price, lead.currency)}</div>
                  </td>
                  <td>
                    <div>{lead.source || '—'}</div>
                    <div className="customer-email">{lead.goal || ''}</div>
                  </td>
                  <td>
                    <div>{lead.location || '—'}</div>
                    {lead.sampleDate && (
                      <div className="customer-email">
                        {t('admin:leadsSampleLabel')} {lead.sampleDate}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className={getStatusBadgeClass(lead.status)}>{getStatusDisplayName(lead.status)}</span>
                    {lead.paidAt && <div className="customer-email">{formatDate(lead.paidAt)}</div>}
                  </td>
                  <td>
                    <div className="order-date">{formatDate(lead.createdAt)}</div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {loading && leads.length > 0 && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}
    </div>
  );
};
